import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { groupBy } from 'lodash';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  deleteFile,
  fetchFile,
  fetchFiles,
  updateData,
} from '../../api/tabsApi';
import { useHandleUpload } from '../tabs/SubTabs';
import DocumentTable from '../tabs/DocumentTable';
import { TState, useActions } from '../../redux/store';
import Loader from '../common/Loader';
import { useCanI } from '../../redux/actions';
import PDFSplit from '../import/PDFSplit';
import { FileViewer } from './FileViewer';

const FileTab = () => {
  const rowData = useSelector((state: TState) => state.rowData);
  const documentList = useSelector((state: TState) => state.documentList);
  const currentTab = useSelector((state: TState) => state.currentTab);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getDataDetail } = useActions();
  const canI = useCanI();

  const handleUpload = useHandleUpload();

  const [files, setFiles] = useState<
    (File & { _id: string; filename?: string })[]
  >([]);
  const [modalFilesId, setModalFilesId] = useState<string>('');
  const [curr, setCurr] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});

  const groupFiles = groupBy(files, 'documentId');
  const modalFiles = groupFiles[modalFilesId] || [];

  const file = modalFiles[curr] || {};
  const fileUrl = fileUrls[file._id];
  const canDelete = canI(modalFilesId, 'delete');
  const canDownload = canI(modalFilesId, 'download');

  const getFiles = useCallback(async () => {
    if (rowData._id) {
      setFiles([]);
      setIsLoading(true);

      const resp = await fetchFiles(rowData._id);

      setFiles(resp.data);
      setIsLoading(false);
    }
  }, [rowData._id]);

  const handleDelete = async () => {
    await deleteFile(file._id);
    getDataDetail({ rowId: rowData._id });
    getFiles();
    setCurr(0);
  };

  useEffect(() => {
    rowData?._id && getFiles();
  }, [rowData._id]);

  useEffect(() => {
    if (!file?._id || fileUrl) return;

    // Récupérer l'URL du fichier depuis le serveur backend
    const getFile = async () => {
      try {
        const respData = await fetchFile(file._id);

        // Créer une URL blob pour l'objet reçu
        const url = URL.createObjectURL(respData);
        setFileUrls((f) => ({ ...f, [file._id]: url }));
      } catch (error) {
        console.error('Erreur lors de la récupération du fichier:', error);
      }
    };

    getFile();
  }, [file?._id]);

  useEffect(() => {
    if (!isOpen) {
      setModalFilesId('');
      setFileUrls({});
      setCurr(0);
    }
  }, [isOpen]);

  const filteredDocumentList = documentList.filter(
    (doc) =>
      !doc.pages ||
      doc.pages.includes(`${currentTab?._id}-show`) ||
      (currentTab?.isPlanning
        ? doc.pages.includes(`planning-show`)
        : doc.pages.includes(`tabs-show`)),
  );

  return (
    <>
      {isLoading && <Loader />}
      <DocumentTable
        data={filteredDocumentList}
        onView={(dataId: any) => {
          setCurr(0);
          setModalFilesId(dataId);
          onOpen();
        }}
        onUpload={async (dataId: string, file: any) => {
          await handleUpload(file, dataId);
          getDataDetail({ rowId: rowData._id });
          getFiles();
        }}
        onUpdate={async (payload: any) => {
          await updateData(payload);
          getDataDetail({ rowId: rowData._id });
        }}
        groupFiles={groupFiles}
      />
      <Modal isOpen={isOpen} onClose={onClose} size='full'>
        <ModalOverlay />
        <ModalContent maxH='100vh' flexDirection='column'>
          <ModalHeader h='62px'>
            {filteredDocumentList?.find((dl) => dl._id === modalFilesId)?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {fileUrl && (
              <FileViewer
                curr={curr}
                setCurr={setCurr}
                fileUrl={fileUrl}
                files={modalFiles}
                canDelete={canDelete}
                canDownload={canDownload}
                handleDelete={handleDelete}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <PDFSplit handleRefresh={getFiles} />
    </>
  );
};

export default FileTab;
